export const PDF_MAX_SIZE_BYTES = 15 * 1024 * 1024

export const PDF_EXTRACT_STEPS = [
    { label: 'Lecture du document', untilMs: 7_000 },
    { label: 'Extraction des lignes de prêt', untilMs: 24_000 },
    { label: 'Vérification par vos soins', untilMs: Number.POSITIVE_INFINITY },
] as const

export const PDF_EXTRACT_TICK_MS = 250
export const PDF_EXTRACT_SLOW_HINT_MS = 45_000
export const PDF_EXTRACT_TIMEOUT_MS = 120_000
export const PDF_EXTRACT_SUCCESS_REVEAL_MS = 600

// L'appel est synchrone et opaque : la progression est estimée sur la durée écoulée, puis plafonnée
export const PDF_EXTRACT_ESTIMATED_MS = 30_000
export const PDF_EXTRACT_PROGRESS_CAP = 0.92
export const PDF_EXTRACT_MIN_REMAINING_S = 3

// Aligné sur MAX_EXTRACTION_ATTEMPTS_PER_TOKEN (LoanComparatorPdfExtractController)
export const PDF_EXTRACT_MAX_ATTEMPTS = 3

export const PDF_EXTRACT_SLOW_HINT = "L'analyse prend un peu plus de temps que prévu, encore quelques secondes…"
export const PDF_EXTRACT_ALMOST_DONE = 'Encore quelques secondes — ne fermez pas cette page.'
export const PDF_EXTRACT_FILLING = 'Lecture terminée, pré-remplissage des champs…'
export const PDF_EXTRACT_LOCKED_HINT = "Renseignez d'abord la date de signature pour lancer la lecture"
export const PDF_EXTRACT_LEAVE_WARNING =
    "L'analyse de votre offre est en cours. Si vous quittez la page, elle sera perdue."

export const pdfExtractRemainingLabel = (seconds: number) =>
    `Environ ${seconds} secondes restantes — les champs s'ouvriront dès la fin de la lecture. Ne fermez pas cette page.`

export class LoanPdfExtractError extends Error {
    code: string

    constructor(code: string, message?: string) {
        super(message || code)
        this.name = 'LoanPdfExtractError'
        this.code = code
    }
}

export interface PdfExtractErrorContent {
    title: string
    body: string
    canRetry: boolean
}

const GENERIC_ERROR: PdfExtractErrorContent = {
    title: "Nous n'avons pas pu lire ce document.",
    body: "Vérifiez que le fichier est un PDF (offre de prêt ou tableau d'amortissement) de moins de 15 Mo – ou passez en saisie manuelle.",
    canRetry: true,
}

const EXPIRED_SESSION_ERROR: PdfExtractErrorContent = {
    title: 'Votre session a expiré.',
    body: "Rechargez la page pour reprendre votre devis, puis réessayez l'import.",
    canRetry: false,
}

export const PDF_EXTRACT_ERRORS: Record<string, PdfExtractErrorContent> = {
    RATE_LIMIT_EXCEEDED: {
        title: "Vous avez atteint la limite d'analyses pour ce devis.",
        body: 'Trois documents ont déjà été analysés. Nous passons en saisie manuelle : les champs restent modifiables à tout moment.',
        canRetry: false,
    },
    ACCESS_DENIED: EXPIRED_SESSION_ERROR,
    QUOTE_NOT_FOUND: EXPIRED_SESSION_ERROR,
    APPLIED_BUT_NOT_REFRESHED: {
        title: 'Votre offre a bien été analysée.',
        body: "L'affichage n'a pas pu être mis à jour automatiquement. Rechargez la page pour retrouver vos données pré-remplies.",
        canRetry: false,
    },
    // L'abort est purement client : le backend a pu terminer, un Réessayer brûlerait une tentative pour rien
    TIMEOUT: {
        title: "L'analyse a pris trop de temps.",
        body: 'Rechargez la page : si le document a tout de même été lu, vos données y seront. Sinon, passez en saisie manuelle.',
        canRetry: false,
    },
    NO_LOAN_FOUND: {
        title: "Aucune ligne de prêt n'a été trouvée dans ce document.",
        body: "Vérifiez qu'il s'agit bien de votre offre de prêt (ou de son tableau d'amortissement) – ou passez en saisie manuelle.",
        canRetry: true,
    },
    FILE_INVALID_TYPE: {
        title: "Ce format n'est pas pris en charge.",
        body: 'Importez votre offre de prêt au format PDF.',
        canRetry: true,
    },
    PAYLOAD_TOO_LARGE: {
        title: 'Ce document est trop volumineux pour être envoyé.',
        body: 'Compressez-le, ou passez en saisie manuelle. Si le problème persiste, signalez-le : la limite du serveur est peut-être trop basse.',
        canRetry: true,
    },
    FILE_TOO_LARGE: {
        title: 'Ce fichier dépasse 15 Mo.',
        body: 'Compressez-le ou passez en saisie manuelle.',
        canRetry: true,
    },
}

export const getPdfExtractError = (code?: string): PdfExtractErrorContent =>
    (code && PDF_EXTRACT_ERRORS[code]) || GENERIC_ERROR
